import React from 'react';
import { CheckCircle } from 'lucide-react';
import HowWeWork from '../../components/HowWeWork';
import PricingSection from '../../components/PricingSection';
import TestimonialsSection from '../../components/TestimonialsSection';
import FAQSection from '../../components/FAQSection';
import CTASection from '../../components/CTASection';
import ContactForm from '../../components/ContactForm';
import PageMeta from '../../components/PageMeta';
import { Card, CardContent } from '../../components/ui/card';
import { Toaster } from '../../components/ui/toaster';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

const ServiceTemplate = ({ 
  title, 
  subtitle, 
  description, 
  heroImage,
  aboutImage,
  usps,
  whyChooseContent,
  serviceId,
  customFAQs,
  customTestimonials,
  customSection,
  customPricing,
  hidePricing = false,
  metaTitle,
  metaDescription
}) => {
  return (
    <div className="min-h-screen pt-20">
      <PageMeta
        title={metaTitle || `${title} | Trademark Vector`}
        description={metaDescription || description}
      />
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#FDFEFE] to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#C0392B]/5 rounded-full blur-3xl" />
        <div className="container mx-auto px-4 relative z-10 max-w-[1320px]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Lead Form First on Mobile */}
            <div className="relative rounded-2xl shadow-2xl bg-white p-6 md:p-8 border-2 border-[#C0392B]/20 order-1 lg:order-2">
              <h2 className="text-2xl md:text-3xl font-bold text-[#2C3E50] mb-2 text-center">
                Get a {title} Quote
              </h2>
              <p className="text-[#7F8C8D] mb-6 text-center">
                Fill out the form and our {title.toLowerCase()} experts will contact you within 24 hours
              </p>
              <ContactForm isCompact={true} packageTitle={title} submitText={`Get ${title} Quote`} />
            </div>

            {/* Text Content */}
            <div className="space-y-6 order-2 lg:order-1">
              <h1 className="text-5xl md:text-6xl font-bold text-[#2C3E50] leading-tight">
                {title}
              </h1>
              <p className="text-2xl text-[#C0392B] font-semibold">{subtitle}</p>
              <p className="text-xl text-[#2C3E50] leading-relaxed">{description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Service */}
      <section className="py-20 bg-gradient-to-br from-[#FDFEFE] via-white to-[#FEF5F5] relative overflow-hidden">
        {/* Decorative shapes */}
        <div className="absolute top-10 right-10 w-64 h-64 bg-[#C0392B]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-64 h-64 bg-[#7F8C8D]/5 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Image */}
            <div className="relative animate-in fade-in slide-in-from-left duration-700">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[400px]">
                <img
                  src={aboutImage}
                  alt={`About ${title}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#C0392B]/20 to-transparent" />
              </div>
            </div>

            {/* Right: Content */}
            <div className="space-y-6 animate-in fade-in slide-in-from-right duration-700 delay-200">
              <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50]">
                About {title}
              </h2>
              <p className="text-xl text-[#2C3E50] leading-relaxed">
                {whyChooseContent.about}
              </p>
            </div>
          </div>
        </div>
      </section>

      <HowWeWork />

      {customSection && customSection}

      {/* Pricing Section */}
      {!hidePricing && (
        customPricing ? (
          customPricing
        ) : (
          <section className="py-20 bg-gradient-to-br from-[#C0392B] via-[#A93226] to-[#8B2E1F] relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '40px 40px'}} />
            </div>
            
            <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
              <div className="text-center mb-16 space-y-4">
                <h2 className="text-4xl md:text-5xl font-bold text-white">
                  Choose Your Package
                </h2>
                <p className="text-xl text-white/90 max-w-3xl mx-auto">
                  Select the perfect package for your needs. All packages include our satisfaction guarantee.
                </p>
              </div>
              <PricingSection service={serviceId} />
            </div>
          </section>
        )
      )}

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-b from-white to-[#FDFEFE] relative overflow-hidden">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, #C0392B 1px, transparent 1px)', backgroundSize: '40px 40px'}} />
        </div>
        
        <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C3E50] leading-tight mb-6">
              Why Choose Trademark Vector for {title}?
            </h2>
          </div>

          <div className="max-w-5xl mx-auto mb-16 space-y-3">
            <p className="text-lg text-[#2C3E50] leading-relaxed">
              {whyChooseContent.paragraph1}
            </p>
            <p className="text-lg text-[#2C3E50] leading-relaxed">
              {whyChooseContent.paragraph2}
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {usps.map((usp, index) => (
              <Card key={index} className="border-[#7F8C8D]/20 hover:border-[#C0392B]/50 transition-all duration-500 hover:shadow-2xl card-lift group">
                <CardContent className="p-8">
                  <div className="flex items-start space-x-4">
                    <CheckCircle className="w-8 h-8 text-[#C0392B] flex-shrink-0 mt-1 group-hover:scale-125 group-hover:rotate-12 transition-all duration-300" />
                    <div className="space-y-3">
                      <h3 className="text-xl md:text-2xl font-bold text-[#2C3E50] group-hover:text-[#C0392B] transition-colors duration-300">{usp.title}</h3>
                      <p className="text-sm md:text-base text-[#2C3E50] leading-relaxed transition-colors duration-300">{usp.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden max-w-6xl mx-auto">
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              className="pb-12"
            >
              {usps.map((usp, index) => (
                <SwiperSlide key={index}>
                  <Card className="border-[#7F8C8D]/20 hover:border-[#C0392B]/50 transition-all duration-500 hover:shadow-2xl card-lift group">
                    <CardContent className="p-8">
                      <div className="flex items-start space-x-4">
                        <CheckCircle className="w-8 h-8 text-[#C0392B] flex-shrink-0 mt-1 group-hover:scale-125 group-hover:rotate-12 transition-all duration-300" />
                        <div className="space-y-3">
                          <h3 className="text-xl md:text-2xl font-bold text-[#2C3E50] group-hover:text-[#C0392B] transition-colors duration-300">{usp.title}</h3>
                          <p className="text-sm md:text-base text-[#2C3E50] leading-relaxed transition-colors duration-300">{usp.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>

      <TestimonialsSection />
      <FAQSection />
      <CTASection showOffer={true} />

      {/* Contact Form */}
      <section id="contact-form" className="py-20 bg-gradient-to-br from-[#2C3E50] to-[#34495E] relative overflow-hidden">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'linear-gradient(45deg, #C0392B 25%, transparent 25%), linear-gradient(-45deg, #C0392B 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #C0392B 75%), linear-gradient(-45deg, transparent 75%, #C0392B 75%)', backgroundSize: '60px 60px', backgroundPosition: '0 0, 0 30px, 30px -30px, -30px 0px'}} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12 space-y-4">
              <h2 className="text-4xl md:text-5xl font-bold text-white">
                Get Started Today
              </h2>
              <p className="text-xl text-white/80">
                Fill out the form below and our experts will contact you within 24 hours
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12 border-2 border-[#C0392B]/20">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <Toaster />
    </div>
  );
};

export default ServiceTemplate;
